// src/services/commonApiSlice.ts
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithRefreshTokenHandler } from "../authFetchBaseQuery";
import {
  API_GET_USER_PREFERENCES,
  API_POST_EVEREST_QUERY,
  API_POST_USER_PREFERENCES,
} from "../ApiConstants";
import { UserPreferenceResponse } from "../../common/UserPreference/models/UserPreferenceResponse";
import { UserPreferenceRequest } from "../../common/UserPreference/models/UserPreferenceRequest";

/**
 * RTK Query API slice for handling common data fetching operations.
 *
 * This slice provides standardized API endpoints for fetching countries,
 * states, merchants, and managing user preferences.
 *
 * @remarks
 * - Uses a shared `baseQueryWithRefreshTokenHandler` to automatically handle token refresh logic.
 * - Defines `tagTypes` for cache invalidation across related endpoints.
 * - Sets `keepUnusedDataFor` to `0` to prevent caching when data is not in use.
 */
export const commonApiSlice = createApi({
  reducerPath: "commonApiSlice",
  baseQuery: baseQueryWithRefreshTokenHandler,
  tagTypes: ["States", "Countries"],
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,

  endpoints: (builder) => ({
    /**
     * Fetches a list of states for a given country from the Everest service.
     *
     * @remarks
     * This query posts a dynamic request body to the Everest query endpoint
     * and retrieves the list of states based on provided parameters.
     *
     * @param request - The request payload containing filters or query structure.
     * @returns A list of states matching the criteria.
     */
    getCountryStates: builder.query({
      query: (request: any) => {
        return {
          url: API_POST_EVEREST_QUERY,
          method: "POST",
          body: JSON.stringify(request),
        };
      },
      providesTags: ["States"],
    }),

    /**
     * Fetches a list of countries from the Everest service.
     *
     * @remarks
     * This endpoint is primarily used to populate dropdowns or filters
     * for country selection within the application.
     *
     * @returns A list of available countries.
     */
    getCountries: builder.query({
      query: () => {
        return {
          url: API_POST_EVEREST_QUERY,
          method: "POST",
          body: JSON.stringify({ query: "Countries", queryBody: "" }),
        };
      },
      providesTags: ["Countries"],
    }),

    /**
     * Fetches all merchants configured in the Perseus Pay system.
     *
     * @remarks
     * Returns a list of merchant records used for onboarding or
     * management within the admin panel.
     *
     * @returns A list of merchants.
     */
    getMerchants: builder.query({
      query: () => {
        return {
          url: API_POST_EVEREST_QUERY,
          method: "POST",
          body: JSON.stringify({
            query: "PerseusPay.GetMerchants",
            queryBody: "",
          }),
        };
      },
    }),

    /**
     * Retrieves the stored user preferences for the currently authenticated user.
     *
     * @remarks
     * User preferences typically include grid layout, column visibility,
     * sorting, filters, theme, and other personalized settings.
     *
     * @returns The user's saved preferences as JSON.
     */
    getUserPreference: builder.query({
      query: () => {
        return {
          url: API_GET_USER_PREFERENCES,
          method: "GET",
        };
      },
    }),

    /**
     * Persists user preference updates to the backend.
     *
     * @remarks
     * This mutation is triggered when the user modifies a grid layout,
     * theme, or other customizable settings. It sends a serialized JSON
     * payload to be saved server-side.
     *
     * @param payload - The request body containing the serialized preferences JSON.
     * @returns A confirmation response after saving.
     */
    saveUserPreference: builder.mutation<UserPreferenceResponse, UserPreferenceRequest>({
      query: (payload) => {
        return {
          url: API_POST_USER_PREFERENCES,
          method: "POST",
          body: payload,
        };
      },
    }),
  }),
});

/**
 * Auto-generated RTK Query hooks for consuming the defined endpoints.
 *
 * @remarks
 * Each exported hook provides ready-to-use query or mutation functions
 * to be used directly within React components.
 *
 * - `useGetCountryStatesQuery` — Fetches states list.
 * - `useGetCountriesQuery` — Fetches countries list.
 * - `useGetMerchantsQuery` — Fetches merchants list.
 * - `useGetUserPreferenceQuery` — Fetches user preferences.
 * - `useLazyGetUserPreferenceQuery` — Lazy version for conditional fetching.
 * - `useSaveUserPreferenceMutation` — Saves user preferences.
 */
export const {
  useGetCountryStatesQuery,
  useGetCountriesQuery,
  useGetMerchantsQuery,
  useGetUserPreferenceQuery,
  useLazyGetUserPreferenceQuery,
  useSaveUserPreferenceMutation,
} = commonApiSlice;
